import { useEffect, useRef, useState } from 'react';

type Position = {
  x: number;
  y: number;
};

type MenuState = {
  visible: boolean;
  position: Position;
};

const SPACEBAR_DELAY_MS = 150;
const MENU_EDGE_PADDING = 40;

function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'TEXTAREA' || tag === 'INPUT';
}

function getMenuPosition(
  cursor: Position,
  menuSize: { width: number; height: number },
) {
  const { innerWidth, innerHeight } = globalThis;
  let x = cursor.x;
  let y = cursor.y;

  // Clamp so menu stays within viewport, keeping cursor at center
  // Add padding to account for border/shadow and ensure full visibility
  const halfWidth = menuSize.width / 2 + MENU_EDGE_PADDING;
  const halfHeight = menuSize.height / 2 + MENU_EDGE_PADDING;

  // Clamp X
  if (x - halfWidth < 0) x = halfWidth;
  if (x + halfWidth > innerWidth) x = innerWidth - halfWidth;

  // Clamp Y
  if (y - halfHeight < 0) y = halfHeight;
  if (y + halfHeight > innerHeight) y = innerHeight - halfHeight;

  return { x, y };
}

export const useFloatingMenu = (menuSize = { width: 724, height: 120 }) => {
  const [menu, setMenu] = useState<MenuState>({
    visible: false,
    position: { x: 0, y: 0 },
  });
  const mousePosRef = useRef<Position>({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
  const spacebarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
    };

    globalThis.addEventListener('mousemove', handleMouseMove);
    return () => {
      globalThis.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Handle spacebar key events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || isEditableElement(e.target)) return;
      e.preventDefault();
      if (e.repeat) return;
      if (menu.visible || spacebarTimerRef.current) return;

      spacebarTimerRef.current = setTimeout(() => {
        // Calculate safe position using tracked mouse position
        const safePos = getMenuPosition(mousePosRef.current, menuSize);
        setMenu({
          visible: true,
          position: safePos,
        });
        spacebarTimerRef.current = null;
      }, SPACEBAR_DELAY_MS);
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;

      if (spacebarTimerRef.current) {
        clearTimeout(spacebarTimerRef.current);
        spacebarTimerRef.current = null;
      }

      setMenu((m) => ({ ...m, visible: false }));
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    globalThis.addEventListener('keyup', handleKeyUp);

    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
      globalThis.removeEventListener('keyup', handleKeyUp);
      if (spacebarTimerRef.current) {
        clearTimeout(spacebarTimerRef.current);
      }
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [menu.visible]);

  const closeMenu = () => {
    setMenu((m) => ({ ...m, visible: false }));
  };

  return {
    menu,
    closeMenu,
  };
};
